import type { ColumnFiltersState } from '@tanstack/react-table'
import type { ColumnDefinition } from '../../Table/types'
import type { SavedView, SavedViewConfig } from '../../../../app/lib/api/saved-views'

export type SavedViewSnapshot = {
  columnFilters: ColumnFiltersState
  search: string
  sort: { sort_by: string; order: 'asc' | 'desc' } | null
  visibleFilterIds: string[]
}

function normalizeFilterValue(value: unknown): unknown {
  if (Array.isArray(value)) {
    return [...value].map(String).sort()
  }
  return value
}

/** Stable JSON for equality of snapshot fields. */
export function normalizeSnapshot(snapshot: SavedViewSnapshot): string {
  const filters = [...(snapshot.columnFilters ?? [])]
    .map((f) => ({
      id: f.id,
      value: normalizeFilterValue(f.value),
    }))
    .sort((a, b) => a.id.localeCompare(b.id))

  const visible = [...(snapshot.visibleFilterIds ?? [])].sort()
  const search = (snapshot.search ?? '').trim()
  const sort = snapshot.sort
    ? { sort_by: snapshot.sort.sort_by, order: snapshot.sort.order }
    : null

  return JSON.stringify({ filters, search, sort, visible })
}

export function snapshotFromPrefs(input: {
  columnFilters: ColumnFiltersState
  search?: string
  sort?: { sort_by: string; order: 'asc' | 'desc' } | null
  visibleFilterIds: string[]
}): SavedViewSnapshot {
  return {
    columnFilters: input.columnFilters ?? [],
    search: input.search ?? '',
    sort: input.sort ?? null,
    visibleFilterIds: input.visibleFilterIds ?? [],
  }
}

export function configFromSnapshot(snapshot: SavedViewSnapshot): SavedViewConfig {
  return {
    columnFilters: snapshot.columnFilters.map((f) => ({
      id: f.id,
      value: f.value,
    })),
    search: snapshot.search,
    sort: snapshot.sort,
    visibleFilterIds: snapshot.visibleFilterIds,
  }
}

export function snapshotFromConfig(config: SavedViewConfig | null | undefined): SavedViewSnapshot {
  const filters = (config?.columnFilters ?? []).map((f) => ({
    id: f.id,
    value: f.value,
  })) as ColumnFiltersState
  return {
    columnFilters: filters,
    search: config?.search ?? '',
    sort: config?.sort ?? null,
    visibleFilterIds: config?.visibleFilterIds ?? [],
  }
}

/**
 * Apply a saved config onto a list: drop unknown filter ids and intersect
 * visibleFilterIds with filterable columns on this list.
 */
export function applySnapshotToPrefs<TRow>(
  config: SavedViewConfig,
  columns: ColumnDefinition<TRow>[],
): SavedViewSnapshot {
  const filterableIds = new Set(
    columns.filter((c) => c.filterType).map((c) => c.id),
  )
  const columnFilters = (config.columnFilters ?? [])
    .filter((f) => filterableIds.has(f.id))
    .map((f) => ({ id: f.id, value: f.value })) as ColumnFiltersState

  const visibleFilterIds = (config.visibleFilterIds ?? []).filter((id) =>
    filterableIds.has(id),
  )

  // Ensure active filter chips remain visible
  for (const f of columnFilters) {
    if (!visibleFilterIds.includes(f.id)) visibleFilterIds.push(f.id)
  }

  return {
    columnFilters,
    search: config.search ?? '',
    sort: config.sort ?? null,
    visibleFilterIds,
  }
}

export function snapshotsEqual(a: SavedViewSnapshot, b: SavedViewSnapshot): boolean {
  return normalizeSnapshot(a) === normalizeSnapshot(b)
}

export function findMatchingView(
  views: SavedView[],
  current: SavedViewSnapshot,
): SavedView | null {
  const needle = normalizeSnapshot(current)
  for (const view of views) {
    if (normalizeSnapshot(snapshotFromConfig(view.config)) === needle) {
      return view
    }
  }
  return null
}
